import { useEffect, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { IconCheck, IconChevronDown, IconLayoutList } from "@tabler/icons-react";
import {
  EDL_STANDARD,
  useResolvedEntityDetailLayout,
  type DetailLayoutEntity,
} from "./detailLayouts";

/**
 * Small header dropdown to switch which entity-popup layout THIS user sees
 * ("Standard" + the event's named layouts). Twin of
 * `GuestDetailLayoutPicker`, same wording (`guest-details` namespace).
 *
 * The pick is a per-user, per-view-context preference (localStorage) —
 * cosmetic, so every role including VIEWER may use it. Renders nothing while
 * the query is in flight or when the event has no named layouts (there would
 * be nothing to switch to).
 */
export function EntityDetailLayoutPicker({
  eventId,
  entity,
  disabled = false,
  disabledTitle,
}: {
  eventId?: string;
  entity: DetailLayoutEntity;
  /** Locked while the host popup has unsaved edits. Switching layouts loses
   *  nothing — the edit stays in `form` and still saves — but a layout that
   *  HIDES the field just typed into would make that edit invisible while it
   *  keeps being written, so the next save stores a value nobody can see.
   *  Locking is honest here; a "discard changes?" prompt would be a lie. */
  disabled?: boolean;
  /** Why it is locked (tooltip). Required in spirit whenever `disabled`. */
  disabledTitle?: string;
}) {
  const t = useTranslations("guest-details");
  const { namedLayouts, selectedId, select, isPending } =
    useResolvedEntityDetailLayout(eventId, entity);
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  // Close on outside click — the popup body has many nested click targets.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener("pointerdown", onPointerDown);
    return () => window.removeEventListener("pointerdown", onPointerDown);
  }, [open]);

  if (!eventId || isPending || namedLayouts.length === 0) return null;

  const entries = [
    { id: EDL_STANDARD, name: t("layoutStandardName") },
    ...namedLayouts.map((l) => ({ id: l.id, name: l.name })),
  ];
  const selectedName =
    entries.find((e) => e.id === selectedId)?.name ?? t("layoutStandardName");

  /** Roving keyboard control over the open menu. Escape closes ONLY the menu:
   *  this widget lives in popups whose window-level Escape closes the whole
   *  editor, and `aria-expanded` below is what tells such a host to stand down
   *  (HubDialogShell.OVERLAY_OPEN) — without it, Escape with the menu open
   *  discarded the user's edits. */
  const onMenuKeyDown = (e: React.KeyboardEvent) => {
    if (!open) return;
    if (e.key === "Escape") {
      e.stopPropagation();
      setOpen(false);
      triggerRef.current?.focus();
      return;
    }
    const items = Array.from(
      containerRef.current?.querySelectorAll<HTMLButtonElement>(
        '[role="menuitemradio"]',
      ) ?? [],
    );
    if (items.length === 0) return;
    const at = items.indexOf(document.activeElement as HTMLButtonElement);
    const go = (i: number) => {
      e.preventDefault();
      items[(i + items.length) % items.length]?.focus();
    };
    if (e.key === "ArrowDown") go(at + 1);
    else if (e.key === "ArrowUp") go(at <= 0 ? items.length - 1 : at - 1);
    else if (e.key === "Home") go(0);
    else if (e.key === "End") go(items.length - 1);
  };

  return (
    <div
      ref={containerRef}
      className="relative"
      data-testid={`entity-layout-picker-${entity}`}
      // The whole container, not just the trigger: the menu is a SIBLING, so
      // focus on a menu item would otherwise find no owner and a capture-phase
      // host would close the popup instead of the menu.
      data-escape-owner={open ? "true" : undefined}
      onKeyDown={onMenuKeyDown}
    >
      <button
        type="button"
        ref={triggerRef}
        title={disabled ? (disabledTitle ?? t("layoutPickerTitle")) : t("layoutPickerTitle")}
        disabled={disabled}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((prev) => !prev)}
        className={`flex flex-row items-center gap-1 px-1.5 py-1 rounded-lg font-tertiary transition-colors ${
          disabled
            ? "opacity-40 cursor-default"
            : "cursor-pointer hover:font-primary hover:surface-tertiary"
        }`}
      >
        <IconLayoutList size={16} className="shrink-0" />
        <span className="body-4-medium max-w-32 truncate">{selectedName}</span>
        <IconChevronDown
          size={14}
          className={`shrink-0 transition-transform duration-200 ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>
      {open && (
        <ul
          role="menu"
          aria-label={t("layoutPickerTitle")}
          className="absolute right-0 top-full mt-1 z-20 min-w-44 max-h-72 overflow-y-auto surface-primary shadow-lg rounded-lg border outline-quaternary py-1"
        >
          {entries.map((entry) => (
            <li key={entry.id} role="presentation">
              <button
                type="button"
                role="menuitemradio"
                aria-checked={entry.id === selectedId}
                data-testid={`entity-layout-option-${entry.id}`}
                onClick={() => {
                  select(entry.id);
                  setOpen(false);
                }}
                className="w-full flex flex-row items-center justify-between gap-2 px-3 py-1.5 body-4-regular font-secondary hover:surface-tertiary cursor-pointer text-left"
              >
                <span className="truncate">{entry.name}</span>
                {entry.id === selectedId && (
                  <IconCheck size={14} className="shrink-0" />
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
